import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { Info, Minus, Plus } from 'lucide-react';
import { BottomSheet, Button, Popover } from '@/components/ui';
import { cn } from '@/lib/utils';
import { usePredictionMarketConfigResolvePriceCube, useTradingOrderPlaceOrder } from '@/lib/endpoints';
import { invalidateTradingCaches } from '@/lib/trading/invalidate-trading-caches';
import { PlaceOrderDtoAction, PlaceOrderDtoType, PredictionMarketConfigResolvePriceCubeFormat } from '@/lib/models';
import type { PlaeePositionResponseDto } from '@/lib/models';
import { closeSide, type ResolvedPositionLeg } from '@/lib/trading/position';
import { fmtUsd } from '@/lib/trading/format';
import { useTerritory } from '@/lib/territory/useTerritory';
import { useBuyInFooter } from '@/lib/footer/useBuyInFooter';
import { useOpenTerritoryGate } from '@/store/territoryGateStore';
import { useOnboardingGate } from '@/lib/auth/useOnboardingGate';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import { ORDER_FAILURE_MESSAGE, toOrderFailureReason } from '@/lib/trading/order-failure';
import { MarketOutcomePricesSubscriber, targetCents, useOutcomePrices } from './useOutcomePrices';

/** The open position a sell sheet operates on, resolved to its option leg. */
export interface SellPositionContext {
  /** The netted open position being (partially) closed. */
  position: PlaeePositionResponseDto;
  /** The resolved option leg — prices the held contract (live sell price). */
  leg: ResolvedPositionLeg;
  /** Answer label for the header, e.g. "JD Vance" or "Yes". */
  label: string;
}

export interface SellPopupProps {
  /** The position to sell, or `null` when the sheet is closed. */
  sell: SellPositionContext | null;
  /** Close the sheet. */
  onClose: () => void;
}

const CONTRACTS = PredictionMarketConfigResolvePriceCubeFormat.CONTRACTS;

/**
 * Sell-position sheet (Figma node 5692-133403) — market-sells some or all
 * contracts of an open cash position.
 *
 * Sibling of {@link BuyInPopup} / {@link LimitOrderPopup} sharing the
 * `BottomSheet`, quick-add cubes (admin CONTRACTS set), gates (kill switch +
 * territory) and failure mapping. Quantity is editable — type freely (the field
 * may be cleared), or use the stepper / quick-add cubes; it is capped at the
 * held contracts and the Sell CTA stays disabled until it is ≥ 1. The price and
 * payout figures track the live mark price while the sheet is open, and the
 * payout is an estimate — the sell executes at the market price at submit
 * time, not a locked quote.
 */
export function SellPopup({ sell, onClose }: SellPopupProps) {
  const [countStr, setCountStr] = useState('1');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { prices, setPrice } = useOutcomePrices();
  const queryClient = useQueryClient();
  const territory = useTerritory();
  const { data: buyInFooter } = useBuyInFooter(territory?.country?.code);
  const footerHtml = buyInFooter?.contentHtml ?? '';
  const footerClean = footerHtml ? DOMPurify.sanitize(footerHtml, { ADD_ATTR: ['target', 'rel'] }) : '';
  const openTerritoryGate = useOpenTerritoryGate();
  const ensureOrderAuth = useOnboardingGate('order_gate');
  const tradingEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_ORDER_PLACEMENT);
  const placeOrder = useTradingOrderPlaceOrder();

  // Reset entry whenever a different position opens the sheet; default to
  // selling the full held quantity (the one-tap behaviour this sheet replaced).
  const [openedId, setOpenedId] = useState<string | null>(null);
  if (sell && sell.position.positionId !== openedId) {
    setOpenedId(sell.position.positionId);
    setCountStr(String(sell.position.quantity));
    setShowBreakdown(false);
    setError(null);
  }

  const contractCubes = usePredictionMarketConfigResolvePriceCube(
    { format: CONTRACTS },
    { query: { enabled: Boolean(sell) } },
  );

  const open = Boolean(sell);
  if (!sell) {
    return <BottomSheet open={false} onClose={onClose} />;
  }
  const { position, leg, label } = sell;

  const max = position.quantity;
  // Raw typed value (0 when the field is empty); qty is the clamped value the
  // stepper/cubes/submit act on. The input never exceeds `max`, so count>max
  // is impossible — but keep the clamp defensive.
  const count = parseInt(countStr, 10) || 0;
  const qty = Math.min(Math.max(count, 1), max);
  // Live-or-seed sell price of the held side — the "Average contract price".
  const cents = targetCents(leg.target, prices);
  const payout = cents != null && count >= 1 ? (qty * cents) / 100 : null;
  const cubes = contractCubes.data?.values ?? [];

  const canSell = tradingEnabled && count >= 1 && !placeOrder.isPending;

  function setQty(next: number) {
    setCountStr(String(Math.min(Math.max(next, 1), max)));
  }

  // Allow free typing and clearing the field; keep only digits and cap at max.
  function handleCountChange(raw: string) {
    const digits = raw.replace(/\D/g, '');
    if (digits === '') {
      setCountStr('');
      return;
    }
    setCountStr(String(Math.min(parseInt(digits, 10), max)));
  }

  function handleSell() {
    if (!canSell) return;
    // Signup-funnel resume comes before any verification gate (ARC-145).
    if (!ensureOrderAuth()) return;
    if (territory?.blocked) {
      openTerritoryGate({ reason: territory.reason });
      return;
    }
    setError(null);
    placeOrder.mutate(
      {
        data: {
          outcomeId: position.outcomeId,
          side: closeSide(position.side),
          action: PlaceOrderDtoAction.sell,
          type: PlaceOrderDtoType.market,
          count: qty,
        },
      },
      {
        onSuccess: (result) => {
          if (!result.success) {
            setError(ORDER_FAILURE_MESSAGE[toOrderFailureReason(result.errorCode)]);
            return;
          }
          invalidateTradingCaches(queryClient);
          onClose();
        },
        onError: () => {
          setError(ORDER_FAILURE_MESSAGE.network);
        },
      },
    );
  }

  return (
    <BottomSheet open={open} onClose={onClose}>
      <div className="w-full bg-surface max-w-[450px] mx-auto">
        <MarketOutcomePricesSubscriber
          marketId={leg.option.marketId}
          externalMarketId={leg.option.externalMarketId}
          onPrice={setPrice}
        />

        {/* Header */}
        <div className="mb-1 flex items-center justify-center gap-1.5">
          <span className="size-3.5 rounded-full bg-accent" aria-hidden="true" />
          <span className="text-sm font-medium text-accent">Sell Your Position</span>
        </div>
        <p className="mb-4 text-center text-sm">
          <span className="font-semibold text-accent-dark">{label}</span>{' '}
          <span className="text-muted">
            {max.toLocaleString('en-US')} Cash Contract{max === 1 ? '' : 's'}
          </span>
        </p>

        {/* Contracts stepper */}
        <span className="text-sm font-medium text-primary">Contracts</span>
        <div className="mt-1 flex h-11 items-center rounded-pill bg-surface-muted border-[1.5px] border-border">
          <button
            type="button"
            aria-label="Fewer contracts"
            onClick={() => setQty(qty - 1)}
            disabled={qty <= 1}
            className="flex h-full w-14 items-center justify-center text-primary disabled:opacity-40"
          >
            <Minus className="size-4" strokeWidth={3} />
          </button>
          <input
            type="text"
            inputMode="numeric"
            aria-label="Contracts"
            value={countStr}
            onChange={(e) => handleCountChange(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-center text-base font-semibold text-primary tabular-nums outline-none"
          />
          <button
            type="button"
            aria-label="More contracts"
            onClick={() => setQty(qty + 1)}
            disabled={qty >= max}
            className="flex h-full w-14 items-center justify-center text-primary disabled:opacity-40"
          >
            <Plus className="size-4" strokeWidth={3} />
          </button>
        </div>

        {/* Quick-add cubes (admin CONTRACTS set); disabled when they'd overshoot the held quantity. */}
        {cubes.length > 0 && (
          <div className="mt-2.5 grid grid-cols-4 gap-2">
            {cubes.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setQty(qty + value)}
                disabled={qty + value > max}
                className="h-8 rounded-pill border border-border text-primary font-semibold text-xs bg-surface hover:bg-surface-muted active:opacity-70 transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                +{value}
              </button>
            ))}
          </div>
        )}

        {/* Average contract price (live) */}
        <div className="mt-5">
          <p className="text-base font-medium text-primary">Average contract price</p>
          <p className="mt-1 text-base text-primary tabular-nums">{cents != null ? `${cents}¢` : '—'}</p>
        </div>

        {/* Payouts + estimate breakdown */}
        <div className="mt-4 flex items-center justify-center gap-1.5">
          <span className="text-lg font-semibold text-primary">
            Payouts <span className="text-success">{payout != null ? fmtUsd(payout) : '—'}</span>
          </span>
          <Popover
            open={showBreakdown}
            onClose={() => setShowBreakdown(false)}
            align="start"
            className="left-[calc(50%-30px)] -translate-x-1/2 w-[248px] p-4 text-sm text-primary"
            anchor={
              <button
                type="button"
                aria-label="Payout breakdown"
                onClick={() => setShowBreakdown((s) => !s)}
                className="text-muted bg-transparent border-none cursor-pointer"
              >
                <Info className="size-3.5" />
              </button>
            }
          >
            <p className="mb-2 font-medium capitalize">Payout Breakdown</p>
            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between">
                <span>Sell price</span>
                <span className="tabular-nums">{cents != null ? `${cents}¢` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span>Contracts</span>
                <span className="tabular-nums">{qty.toLocaleString('en-US')}</span>
              </div>
            </div>
            <div className="my-2 border-t border-border" />
            <div className="flex justify-between font-medium">
              <span>Payout</span>
              <span className="tabular-nums text-success">{payout != null ? fmtUsd(payout) : '—'}</span>
            </div>
            <p className="mt-2 text-xs text-muted">
              Estimated — your sell executes at the current market price, which can move until it fills.
            </p>
          </Popover>
        </div>

        {/* Sell CTA */}
        <div className="mt-4 mb-6">
          <Button
            variant="default"
            className="w-full h-[42px] rounded-pill text-lg"
            disabled={!canSell}
            onClick={handleSell}
          >
            {placeOrder.isPending ? 'Selling…' : 'Sell'}
          </Button>
        </div>

        {(error || !tradingEnabled) && (
          <p className={cn('text-center text-xs font-medium', error ? 'text-danger-alt' : 'text-muted')}>
            {error ?? 'Trading is temporarily paused'}
          </p>
        )}

        {footerClean && (
          <div
            className="mt-3 text-center text-[10px] leading-snug text-muted"
            dangerouslySetInnerHTML={{ __html: footerClean }}
          />
        )}
      </div>
    </BottomSheet>
  );
}
